import { DateTime } from 'luxon'
import Personnummer from 'personnummer'

import type { ConsentChange } from '@fortum/global-consent-components'

import { getFirstPossibleStartDateOffset } from '@/open-web/components/Checkout/Steps/DeliveryStep/utils'
import {
  DATE_FORMAT_FOR_API,
  FI_RESIDENCE_DEFINED_DEFAULT_HOUSE_LETTER,
} from '@/open-web/utils/constants'
import type { CampaignConfigurationEntry } from '@/shared/contentful/types'
import { type Country, browserEnvs } from '@/shared/envs'
import type {
  ContractDiscountElement,
  ContractsValidationProcess,
} from '@/shared/graphql/schema/commonBackend/graphql'
import { type FullLocale, type Locale, getConsentLocale } from '@/shared/locale'
import type { EnrichedContractTemplate } from '@/shared/services/campaignDataResolver'
import type {
  CheckoutDataState,
  HasActiveContractOnDeliveryAnswer,
  PostDeliveryMethod,
} from '@/shared/store/slices/checkoutDataSlice'
import type { DeliveryAddressType } from '@/shared/store/slices/checkoutFlowSlice'
import type { SelectedAddons } from '@/shared/store/slices/selectedContractSlice'
import type { Address } from '@/shared/store/types'
import { ADDON_TYPE } from '@/shared/utils/addonsUtils'
import { logError } from '@/shared/utils/error'
import { OrderDataAdapterError, UiError } from '@/shared/utils/errorClasses'

import type { Consent, Invoice, Meter, Address as OrderRequestAddress } from './types'

//It is agreed value for soft launch to differ OGW orders from legacy system orders, probably once we do full release should be changed to Online
export const SELLER_NAME = 'OGW-Online'

const HOURLY_MEASUREMENT_PROPERTY = [
  { id: '99', value: '1', customValue: '1', isCustomValue: true },
]

const CONTRACT_SIGN_REASON = {
  MOVING: 1,
  CHANGE_OF_SUPPLIER: 2,
} as const

export const getAddons = (
  availableAddons: EnrichedContractTemplate['availableAddons'],
  selectedAddons: SelectedAddons,
) => {
  return Object.values(selectedAddons)
    .map((selected) => selected.value)
    .filter(Boolean)
    .map((tariffNo) => {
      const addon = availableAddons.find((addon) => addon.tariffNo === tariffNo)

      if (!addon?.templateNo) {
        throw logError(
          new UiError(
            `Something went wrong when mapping addon tariffNo: ${tariffNo} for order payload`,
          ),
        )
      }

      return {
        id: addon.templateNo.toString(),
        name: addon.templateName,
      }
    })
}

export const getContractDiscounts = (discountElements: ContractDiscountElement[]) => {
  return discountElements
    .map((discount) => {
      if (discount.templateNo) {
        return {
          id: discount.templateNo.toString(),
          name: discount.elementName,
        }
      }

      // Only in Sweden discounts for contract should always have templateNo
      if (browserEnvs.NEXT_PUBLIC_COUNTRY === 'SE') {
        throw logError(
          new OrderDataAdapterError(
            `Discount: "${discount.elementName}" has no templateNo. Can not create order payload.`,
          ),
        )
      }
    })
    .filter(Boolean)
}

export const createOrderProducts = (
  contractTemplate: EnrichedContractTemplate,
  selectedAddons: SelectedAddons,
) => [
  { id: contractTemplate.templateNo.toString(), name: contractTemplate.templateName },
  ...getContractDiscounts(contractTemplate.discountElements),
  ...getAddons(contractTemplate.availableAddons, selectedAddons),
]

export const getDefaultStartDate = (
  contractConfiguration: CampaignConfigurationEntry,
  isPoaGranted: boolean,
) =>
  DateTime.now()
    .plus({ days: getFirstPossibleStartDateOffset(isPoaGranted, contractConfiguration) })
    .toFormat(DATE_FORMAT_FOR_API)

const ORDER_METHOD: Record<CheckoutDataState['billing']['method'], number> = {
  'e-invoice': 3,
  post: 1,
  email: 8,
}

export const getInvoice = (
  country: Country,
  billing: CheckoutDataState['billing'],
  email: string,
  isDesignatedSupplier?: boolean,
): Invoice => {
  const { method, address, bank, postDeliveryMethod, shouldSendCombinedInvoice } = billing

  const invoice = {
    code: bank?.bic,
    paymentTypeId: ORDER_METHOD[method],
    address: address ? createAddress(address) : undefined,
    email,
  }

  switch (country) {
    case 'FI':
      return {
        ...invoice,
        address: address ? getFinnishBillingAddress(address, postDeliveryMethod) : undefined,
        includeGridInvoice: isDesignatedSupplier ? shouldSendCombinedInvoice : undefined,
      }
    case 'SE':
    case 'NO':
    case 'GB':
    default:
      return invoice
  }
}

export const getFinnishBillingAddress = (
  address: Address,
  postDeliveryMethod?: PostDeliveryMethod,
): OrderRequestAddress => {
  switch (postDeliveryMethod) {
    case 'postBox':
      return {
        countryCode: address.country,
        street: {
          name: '',
          number: '',
          letter: '',
          residence: '',
        },
        zipCode: address.postalCode,
        city: address.city,
        postBoxOffice: `${address.postBoxNumber}`,
      }
    case 'generalDelivery':
      return {
        countryCode: 'FI',
        street: {
          name: 'Poste Restante',
          number: '',
          letter: '',
          residence: '',
        },
        zipCode: address.postalCode,
        city: address.city,
      }
    case 'streetAddress':
    default:
      return createAddress(address)
  }
}

export const getMeter = (country: Country, meter: CheckoutDataState['meter']): Meter => {
  const { address, meterInformationSource, gridOwnerCode, meterPointId } = meter
  switch (country) {
    case 'SE':
      const meterAddress = {
        address: createAddress(address),
      }
      if (meterInformationSource === 'manualInput') {
        return {
          ...meterAddress,
          meterPointId: meterPointId,
          gln: (gridOwnerCode || '')?.toLocaleUpperCase(),
        }
      }
      return meterAddress
    case 'FI':
      return {
        address: createAddress(address),
        meterPointId,
        meteringGridAreaEicCode: gridOwnerCode,
      }
    case 'NO':
    case 'GB':
      return { address: createAddress(address) }
    //To be expanded with other countries logic once its known
  }
}

const handleHouseLetter = (
  houseLetter?: string,
  residence?: string,
  isForeignAddress?: boolean,
) => {
  if (isForeignAddress) {
    return houseLetter
  }

  if (residence && !houseLetter) {
    return FI_RESIDENCE_DEFINED_DEFAULT_HOUSE_LETTER
  }

  return houseLetter || ''
}

export const createAddress = ({
  postalCode,
  city,
  streetName,
  houseLetter,
  houseNumber,
  residence,
  country: addressCountry,
}: Address): OrderRequestAddress => {
  const country = browserEnvs.NEXT_PUBLIC_COUNTRY
  if (country === 'FI') {
    const isForeignAddress = !!addressCountry && addressCountry !== 'FI'

    return {
      countryCode: isForeignAddress ? addressCountry : country,
      street: {
        name: streetName,
        letter: handleHouseLetter(houseLetter, residence, isForeignAddress),
        number: houseNumber,
        residence,
      },
      zipCode: postalCode,
      city: isForeignAddress ? city : (city ?? '').toUpperCase(),
    }
  }

  return {
    countryCode: addressCountry || country,
    street: {
      name: streetName,
      ...splitHouseNumber(houseNumber),
      residence,
    },
    zipCode: postalCode,
    city,
  }
}

const splitHouseNumber = (houseNumber?: string) => {
  if (!houseNumber) {
    return
  }
  const result = houseNumber.trim().match(/\d+|\w/g)
  if (result !== null) {
    const [number, letter] = result
    return { number, letter }
  }
  return
}

export const getSsnInOrderApiFormat = (ssn: string) => Personnummer.parse(ssn).format(true)

export const mapConsents = (
  consents: ConsentChange[],
  locale: FullLocale,
  openWebTemplateId: string,
): Consent[] | undefined =>
  consents?.length
    ? consents.map((consent) => ({
        frameworkGroupId: consent.groupId,
        //it is done like that becasue of weird value in consent.legalTextLang, we have only one lang in ROST so it is not a problem
        //no additional mapping needed
        locale: getConsentLocale(locale),
        optIn: consent.optState === 'Optin' ? 1 : 0,
        templateId: openWebTemplateId || 'NO_DATA',
        frameworkItemId: consent.frameworkItem,
        // Agreed value with CoOp specialist, it is stored in EnoroCX and used by Customer Service, shouldn't be changed without informing CoOp and Customer Service
        sourceChannel: 'OGW-Online',
      }))
    : undefined

export const getAddonsCustomProperties = (selectedAddons: SelectedAddons) => {
  const isHourlyMeasurementAddonSelected = Boolean(
    selectedAddons[ADDON_TYPE.hourlyMeasurement]?.value,
  )

  if (isHourlyMeasurementAddonSelected) {
    return HOURLY_MEASUREMENT_PROPERTY
  }

  return []
}

export const getEuroBonusProperty = (euroBonus: string) => {
  return [{ id: '68', value: '1', customValue: `EB${euroBonus}`, isCustomValue: true }]
}

//TODO: Double check if the code value should not be presented in property value
export const getCoopProperty = (code: string) => {
  return [{ id: '63', value: '1', customValue: code, isCustomValue: true }]
}

export const getSyMembershipProperty = (syMembership?: string) => {
  if (!syMembership) {
    return []
  }
  // Values:  1 = Active, 2 = Terminated
  const SY_MEMBERSHIP_PROPERTY_VALUE = '1'
  const SY_MEMBERSHIP_PROPERTY_NO = '118'

  return [
    {
      id: SY_MEMBERSHIP_PROPERTY_NO,
      value: SY_MEMBERSHIP_PROPERTY_VALUE,
      isCustomValue: true,
      customValue: syMembership,
    },
  ]
}

export const getLanguageNo = (locale: Locale) => {
  switch (locale) {
    case 'fi':
      return 5
    case 'sv':
      return 4
    case 'nb':
    //TODO: add proper value for Norway, once its known
    case 'en':
    default:
      return 3
  }
}

export const getReasonNewContract = (
  deliveryAddressType?: DeliveryAddressType,
  contractValidationProcess?: ContractsValidationProcess,
  hasActiveContractOnDeliveryAnswer?: HasActiveContractOnDeliveryAnswer,
) => {
  switch (contractValidationProcess) {
    case 'PRODUCT_CHANGE':
      return null
    case 'MOVING':
      return {
        reasonNewContract: {
          reasonNewContractNo: CONTRACT_SIGN_REASON.MOVING,
        },
      }
    case 'UNKNOWN':
    default:
      return {
        reasonNewContract: {
          reasonNewContractNo:
            // 1. If moving selected in the form then we are not asking about the active contract - MOVING
            // 2. If not moving selected, then we ask for active contract
            //    - yes means that it is change of supplier - CHANGE_OF_SUPPLIER
            //    - no means that customer is moving - MOVING
            deliveryAddressType === 'moving' || hasActiveContractOnDeliveryAnswer === 'no'
              ? CONTRACT_SIGN_REASON.MOVING
              : CONTRACT_SIGN_REASON.CHANGE_OF_SUPPLIER,
        },
      }
  }
}

//Finnish SSN handling
export const ssnBreakDown = (ssn: string) => {
  const regex = /^(\d{2})(\d{2})(\d{2})([abcdefyxwvu+-])(\d{3})([a-z0-9])$/i
  const match = ssn.match(regex)
  if (!match) {
    return false
  }

  const [_, day, month, year, centurySign, individual, checksum] = match

  return { day, month, year, centurySign, individual, checksum }
}

const getBirthDateAndAgeFromFinnishSsn = (ssn: string) => {
  const breakDownResult = ssnBreakDown(ssn.toUpperCase())
  if (!breakDownResult) {
    throw new Error('invalid SSN format')
  }

  const { day, month, year, centurySign } = breakDownResult

  type CenturySign = '+' | '-' | 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'Y' | 'X' | 'W' | 'V' | 'U'

  const getCentury = (sign: CenturySign) => {
    if (['+'].includes(sign)) {
      return 1800
    } else if (['-', 'Y', 'X', 'W', 'V', 'U'].includes(sign)) {
      return 1900
    } else if (['A', 'B', 'C', 'D', 'E', 'F'].includes(sign)) {
      return 2000
    }

    throw new Error('failed to get century value from SSN')
  }

  const century = getCentury(centurySign as CenturySign)

  const fullYear = century + Number.parseInt(year)

  const birthDate = `${fullYear}-${month}-${day}`

  return { birthDate, age: getAgeFromBirthDate(day, month, fullYear.toString()) }
}

const getAgeFromBirthDate = (day: string, month: string, fullYear: string) => {
  const dateObj = {
    day: Number.parseInt(day),
    month: Number.parseInt(month),
    year: Number.parseInt(fullYear),
  }

  return Math.trunc(DateTime.now().diff(DateTime.fromObject(dateObj), 'years').years)
}

export const getBirthDateAndAgeFromSsn = (ssn: string) => {
  const country = browserEnvs.NEXT_PUBLIC_COUNTRY
  if (country === 'FI') {
    return getBirthDateAndAgeFromFinnishSsn(ssn)
  }

  if (country === 'SE') {
    const { fullYear, month, day } = new Personnummer(ssn)
    return {
      birthDate: `${fullYear}-${month}-${day}`,
      age: getAgeFromBirthDate(day, month, fullYear),
    }
  }

  throw new Error('getBirthDateFromSsn is not implemented')
}

/**
 * Adapter function to get contract termination details for Finnish orders.
 * This function checks if the active contract number and contract termination details are provided,
 * and returns the contract number and end date based on the termination details.
 * If the termination date is the same as the start date, it uses the start date as the end date.
 * If a specific termination date is provided, it uses that date instead.
 * @param variant
 * @param activeContractNo
 * @param contractTermination
 * @param startDate
 * @returns
 */
export const getContractTerminationDetails = (
  activeContractNo?: number,
  contractTermination?: CheckoutDataState['contractTermination'],
  startDate?: string,
) => {
  if (
    !activeContractNo ||
    (!contractTermination?.isSameAsStartDate && !contractTermination?.date)
  ) {
    return
  }

  if (contractTermination?.isSameAsStartDate) {
    return {
      contractNumber: activeContractNo,
      contractEndDate: startDate,
    }
  }

  // If termination date is set, we have to add one day to it,
  // because the contract end date in the EnoroCX is the first day when contract is not valid anymore.
  // For example, if the contract ends on 2025-08-01, it means that the contract is valid until the end of 2025-07-31 (23:59:59).
  // So we add one day to the termination date to get the correct end date in the EnoroCX.
  const terminationDate = contractTermination?.date
    ? DateTime.fromFormat(contractTermination?.date, DATE_FORMAT_FOR_API)
        .plus({ days: 1 })
        .toFormat(DATE_FORMAT_FOR_API)
    : undefined

  return {
    contractNumber: activeContractNo,
    contractEndDate: terminationDate,
  }
}
